import React, { useMemo, useEffect } from 'react';
import { RecommendationReportWidget } from './RecommendationReportWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import {
  GET_RECOMMENDATION_REPORT,
  GET_PORTFOLIO_NAME,
} from '../../../../graphql/queries/portfolio';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import type { RecommendationReport } from './pdfGenerator';

interface RecommendationReportData {
  EQPortfoliosAIAnalysis: {
    analysis_data: {
      recommendation_report?: string; // This is actually a string, not an array
      recommendations?: Array<{
        action: string;
        ticker: string;
        reasoning: string;
        confidence: 'high' | 'medium' | 'low';
        current_allocation: number;
        recommended_allocation: number;
        reason: string;
        ai_reasoning?: string;
      }>;
      market_metrics?: {
        sector_momentum_breakdown?: Record<
          string,
          { value: number; trend: string }
        >;
        news_sentiment?: { value: number; trend: string };
        volatility_index?: { value: number; trend: string };
      };
      risk_alerts?: Array<{
        title: string;
        message: string;
        level: 'low' | 'medium' | 'high';
        confidence: number;
      }>;
      confidence_score?: number;
      market_predictions?: {
        risk_outlook?: string;
        price_targets?: Record<string, number>;
        sector_outlook?: Record<string, string>;
        confidence_level?: number;
        forecast_horizon_days?: number;
      };
    };
    createdAt: string;
  }[];
}

export interface RecommendationReportWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const RecommendationReportWidgetWrapper: React.FC<
  RecommendationReportWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const snapshotId = latestSnapshotId;

  const { data, loading, error, refetch } = useQuery<RecommendationReportData>(
    GET_RECOMMENDATION_REPORT,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId, // Skip if no snapshot ID
    },
  );

  // Fetch portfolio name
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  const reportsData = useMemo(() => {
    if (loading) {
      return [];
    }

    if (error) {
      return [];
    }

    // If no data at all, return empty array (widget will show "No data" state)
    if (!data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data) {
      return [];
    }

    const analysisData = data.EQPortfoliosAIAnalysis[0].analysis_data;

    // Check if recommendation_report exists and has content
    if (
      analysisData.recommendation_report &&
      analysisData.recommendation_report.trim().length > 0
    ) {
      const reportText = analysisData.recommendation_report;

      const report: RecommendationReport = {
        id: 'ai-analysis-report',
        title: 'AI Portfolio Analysis Report',
        content: reportText,
        key_findings: [],
        recommendations: [],
        generated_at: data.EQPortfoliosAIAnalysis[0].createdAt,
        analyst_notes: '',
        risk_alerts: analysisData.risk_alerts,
      };

      return [report];
    }

    // No recommendation report available
    return [];
  }, [data, loading, error]);

  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const portfolioName = portfolioData?.EQPortfolio?.[0]?.name;

  return (
    <RecommendationReportWidget
      data={reportsData}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      snapshotId={snapshotId}
      portfolioName={portfolioName}
    />
  );
};
